import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { Button } from "../components/ui/button";
import { submitPasswordReset } from "../services/authApi";

/**
 * Public reset-password page. Reached via the emailed link
 * `{base}/reset-password?token=...`. Consumes the single-use token and sets a
 * new password. Accessible while logged out (registered outside ProtectedRoute).
 */
export const ResetPasswordPage: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pw.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (pw !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await submitPasswordReset(token, pw);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reset failed. Request a new link.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary-100 px-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-6">
          <img
            src={`${process.env.PUBLIC_URL || ""}/Rebound_Logo.png`}
            alt="Rebound"
            className="h-14 w-auto mb-3"
          />
          <h1 className="text-lg font-semibold text-secondary-800">Reset password</h1>
        </div>

        <div className="bg-white border border-secondary-200 rounded-lg shadow-sm p-6 space-y-4">
          {done ? (
            <>
              <div className="text-sm text-success-700 bg-success-50 border border-success-200 rounded px-3 py-2">
                Your password has been updated.
              </div>
              <Link
                to="/login"
                className="block text-center text-sm text-primary-700 hover:text-primary-900 hover:underline"
              >
                Back to sign in
              </Link>
            </>
          ) : !token ? (
            <div className="text-sm text-error-700 bg-error-50 border border-error-200 rounded px-3 py-2">
              This reset link is missing its token. Request a new one from the
              sign-in page.
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              {error && (
                <div className="text-sm text-error-700 bg-error-50 border border-error-200 rounded px-3 py-2">
                  {error}
                </div>
              )}
              <div>
                <label
                  htmlFor="pw"
                  className="block text-xs font-medium text-secondary-600 mb-1"
                >
                  New password (8+ characters)
                </label>
                <input
                  id="pw"
                  type="password"
                  autoComplete="new-password"
                  autoFocus
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="w-full min-h-[44px] px-3 py-2 border border-secondary-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label
                  htmlFor="confirm"
                  className="block text-xs font-medium text-secondary-600 mb-1"
                >
                  Confirm new password
                </label>
                <input
                  id="confirm"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full min-h-[44px] px-3 py-2 border border-secondary-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? "Updating…" : "Set new password"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
